"use client";

import { useState } from "react";
import {
  Notice,
  SelectField,
  StatCard,
  StatGrid,
  TextField,
} from "@/components/tool-kit/primitives";
import { RESULT_CARD, WIDGET_H3 } from "@/components/tool-kit/styles";
import type { CurveStrings } from "@/components/tools/curve-fitting-calculator";
import {
  Explanation,
  Formula,
  ResultTable,
  WorkingPanel,
} from "@/components/tools/stats-descriptive/ui";
import { XYPlot } from "@/components/tools/stats-regression/xy-plot";
import { t } from "@/lib/i18n/interpolate";
import { parseNumberInput } from "@/lib/parse-number";
import {
  formatPValue,
  formatStat,
  type NumberFormatter,
} from "@/lib/tool-kit/stats-descriptive/format";
import {
  type CurveFit,
  type ModelId,
  type ModelResult,
  polynomialDegree,
  type Unavailable,
} from "@/lib/tool-kit/stats-regression/curves";

interface Props {
  results: ModelResult[];
  xs: readonly number[];
  ys: readonly number[];
  modelName: (id: ModelId) => string;
  strings: CurveStrings;
  fmt: NumberFormatter;
}

const SUPERSCRIPT = ["", "", "²", "³", "⁴", "⁵", "⁶"];
const SUBSCRIPT = ["₀", "₁", "₂", "₃", "₄", "₅", "₆"];
const CURVE_POINTS = 120;
const SHOWN_RESIDUALS = 15;
const SCI_LOW = 1e-4;
const SCI_HIGH = 1e7;

/** Six significant digits, switching to scientific notation for extreme sizes. */
function sig(fmt: NumberFormatter, value: number): string {
  if (!Number.isFinite(value)) {
    return "—";
  }
  const size = Math.abs(value);
  if (size !== 0 && (size < SCI_LOW || size >= SCI_HIGH)) {
    return fmt(value, { notation: "scientific", maximumSignificantDigits: 5 });
  }
  return fmt(value, { maximumSignificantDigits: 6 });
}

function reasonText(reason: Unavailable, strings: CurveStrings): string {
  const reasons: Record<Unavailable, string> = {
    needsPositiveX: strings.reasonNeedsPositiveX,
    needsPositiveY: strings.reasonNeedsPositiveY,
    needsMoreData: strings.reasonNeedsMoreData,
    needsDistinctX: strings.reasonNeedsDistinctX,
    failed: strings.reasonFailed,
  };
  return reasons[reason];
}

/** "1.2 + 3.4x − 0.5x²": the signed terms of a polynomial. */
function polynomialTerms(
  fit: CurveFit,
  fmt: NumberFormatter,
  strings: CurveStrings
): string {
  let terms = "";
  for (const [k, parameter] of fit.parameters.entries()) {
    const negative = parameter.value < 0;
    const magnitude = sig(fmt, Math.abs(parameter.value));
    const body = k === 0 ? magnitude : `${magnitude}x${SUPERSCRIPT[k] ?? ""}`;
    if (k === 0) {
      terms += `${negative ? "−" : ""}${body}`;
    } else {
      terms += `${negative ? strings.termMinus : strings.termPlus}${body}`;
    }
  }
  return terms;
}

export function equationText(
  fit: CurveFit,
  fmt: NumberFormatter,
  strings: CurveStrings
): string {
  const [a, b] = fit.parameters;
  if (polynomialDegree(fit.id) !== null) {
    return t(strings.eqPoly, { terms: polynomialTerms(fit, fmt, strings) });
  }
  if (fit.id === "exponential") {
    return t(strings.eqExp, { a: sig(fmt, a.value), b: sig(fmt, b.value) });
  }
  if (fit.id === "logarithmic") {
    return t(b.value < 0 ? strings.eqLogMinus : strings.eqLog, {
      a: sig(fmt, a.value),
      b: sig(fmt, Math.abs(b.value)),
    });
  }
  return t(strings.eqPower, { a: sig(fmt, a.value), b: sig(fmt, b.value) });
}

function termLabel(fit: CurveFit, index: number): string {
  if (polynomialDegree(fit.id) === null) {
    return fit.parameters[index].name;
  }
  const sub = SUBSCRIPT[index];
  if (index === 0) {
    return `a${sub}`;
  }
  return `a${sub} · x${SUPERSCRIPT[index] ?? ""}`;
}

function Comparison({ results, modelName, strings, fmt }: Props) {
  const fitted = results.filter((r) => r.fit !== null);
  const aiccs = fitted
    .map((r) => (r.fit as CurveFit).aicc)
    .filter(Number.isFinite);
  const bestAicc = aiccs.length > 0 ? Math.min(...aiccs) : Number.NaN;
  const best = fitted.find((r) => (r.fit as CurveFit).aicc === bestAicc);
  const num = (value: number) => formatStat(fmt, value);
  const failed = results.filter((r) => r.fit === null);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.compareTitle}</h3>
      {fitted.length > 0 ? (
        <ResultTable
          head={[
            strings.colModel,
            strings.colEquation,
            strings.colR2,
            strings.colAdjR2,
            strings.colRmse,
            strings.colAic,
            strings.colAicc,
            strings.colDelta,
          ]}
          rows={fitted.map(({ id, fit }) => {
            const f = fit as CurveFit;
            return [
              modelName(id),
              equationText(f, fmt, strings),
              num(f.r2),
              num(f.adjR2),
              num(f.rmse),
              num(f.aic),
              num(f.aicc),
              Number.isFinite(f.aicc) ? num(f.aicc - bestAicc) : "—",
            ];
          })}
          wrapFirstColumn
        />
      ) : null}
      {best ? (
        <Explanation>
          {t(strings.best, { model: modelName(best.id) })}
        </Explanation>
      ) : null}
      <Explanation>{strings.bestNote}</Explanation>
      {failed.length > 0 ? (
        <Notice>
          <strong>{strings.notFittedTitle}.</strong>{" "}
          {failed
            .map((r) =>
              t(strings.notFittedLine, {
                model: modelName(r.id),
                reason: reasonText(r.reason ?? "failed", strings),
              })
            )
            .join(" ")}
        </Notice>
      ) : null}
    </div>
  );
}

function Detail({
  fit,
  name,
  strings,
  fmt,
  n,
}: {
  fit: CurveFit;
  name: string;
  strings: CurveStrings;
  fmt: NumberFormatter;
  n: number;
}) {
  const num = (value: number) => formatStat(fmt, value);
  return (
    <div className="space-y-4">
      <h3 className={WIDGET_H3}>{t(strings.detailTitle, { model: name })}</h3>
      <div className={`${RESULT_CARD} text-center`}>
        <p className="text-muted-foreground text-sm">{strings.equationLabel}</p>
        <p className="break-words font-mono font-semibold text-foreground text-lg">
          {equationText(fit, fmt, strings)}
        </p>
      </div>
      <div aria-live="polite">
        <StatGrid>
          <StatCard emphasis label={strings.rSquaredCard} value={num(fit.r2)} />
          {fit.transformedR2 === null ? null : (
            <StatCard
              label={strings.rSquaredLogCard}
              value={num(fit.transformedR2)}
            />
          )}
          <StatCard label={strings.rmseCard} value={num(fit.rmse)} />
          <StatCard label={strings.sseCard} value={num(fit.sse)} />
          <StatCard label={strings.nCard} value={fmt(n)} />
          <StatCard label={strings.paramsCard} value={fmt(fit.k)} />
        </StatGrid>
      </div>
      <ResultTable
        head={[
          strings.colTerm,
          strings.colValue,
          strings.colSe,
          strings.colT,
          strings.colP,
        ]}
        rows={fit.parameters.map((p, index) => [
          termLabel(fit, index),
          sig(fmt, p.value),
          sig(fmt, p.se),
          formatStat(fmt, p.t, 3),
          formatPValue(fmt, p.p),
        ])}
      />
      {fit.transformedR2 === null ? null : (
        <Explanation>{strings.transformedNote}</Explanation>
      )}
      {fit.illConditioned && fit.centred ? (
        <Notice>
          {t(strings.illConditioned, {
            centre: sig(fmt, fit.centred.centre),
            scale: sig(fmt, fit.centred.scale),
          })}
        </Notice>
      ) : null}
      {fit.converged ? null : <Notice>{strings.notConverged}</Notice>}
    </div>
  );
}

function Plots({
  fitted,
  detail,
  xs,
  ys,
  modelName,
  strings,
  fmt,
}: {
  fitted: ModelResult[];
  detail: CurveFit;
  xs: readonly number[];
  ys: readonly number[];
  modelName: (id: ModelId) => string;
  strings: CurveStrings;
  fmt: NumberFormatter;
}) {
  const low = Math.min(...xs);
  const high = Math.max(...xs);
  const grid = Array.from(
    { length: CURVE_POINTS },
    (_, i) => low + ((high - low) * i) / (CURVE_POINTS - 1)
  );
  const colorOf = (id: ModelId) => 1 + fitted.findIndex((r) => r.id === id);
  const series = [
    {
      kind: "points" as const,
      xs,
      ys,
      color: 0,
      label: strings.seriesData,
    },
    ...fitted.map((r) => {
      const f = r.fit as CurveFit;
      return {
        kind: "line" as const,
        xs: grid,
        ys: grid.map((x) => f.predict(x)),
        color: colorOf(r.id),
        label: modelName(r.id),
        dashed: r.id !== detail.id,
      };
    }),
  ];
  const name = modelName(detail.id);
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>{strings.plotTitle}</h3>
        <XYPlot
          fmt={fmt}
          label={t(strings.plotLabel, { n: fmt(xs.length) })}
          series={series}
          xLabel={strings.axisX}
          yLabel={strings.axisY}
        />
      </div>
      <div className="space-y-2">
        <h3 className={WIDGET_H3}>
          {t(strings.residualPlotTitle, { model: name })}
        </h3>
        <XYPlot
          fmt={fmt}
          label={t(strings.residualPlotLabel, { model: name })}
          series={[
            {
              kind: "points",
              xs,
              ys: detail.residuals,
              color: 0,
              label: strings.seriesData,
            },
            {
              kind: "line",
              xs: [low, high],
              ys: [0, 0],
              color: colorOf(detail.id),
              label: strings.seriesZero,
              dashed: true,
            },
          ]}
          xLabel={strings.axisX}
          yLabel={strings.axisResidual}
        />
        <Explanation>{strings.residualNote}</Explanation>
      </div>
    </div>
  );
}

function Predict({
  fitted,
  xs,
  modelName,
  strings,
  fmt,
}: {
  fitted: ModelResult[];
  xs: readonly number[];
  modelName: (id: ModelId) => string;
  strings: CurveStrings;
  fmt: NumberFormatter;
}) {
  const [text, setText] = useState("");
  const x = parseNumberInput(text);
  const low = Math.min(...xs);
  const high = Math.max(...xs);
  return (
    <div className={`${RESULT_CARD} space-y-4`}>
      <h3 className={WIDGET_H3}>{strings.predictTitle}</h3>
      <div className="w-48">
        <TextField
          id="cf-predict"
          label={strings.predictLabel}
          numeric
          onChange={setText}
          value={text}
        />
      </div>
      {x === null ? null : (
        <div aria-live="polite" className="space-y-3">
          <ResultTable
            head={[strings.colModel, strings.colPredicted]}
            rows={fitted.map((r) => [
              modelName(r.id),
              sig(fmt, (r.fit as CurveFit).predict(x)),
            ])}
            wrapFirstColumn
          />
          {x < low || x > high ? (
            <Notice>
              {t(strings.extrapolation, {
                x: formatStat(fmt, x),
                min: formatStat(fmt, low),
                max: formatStat(fmt, high),
              })}
            </Notice>
          ) : null}
        </div>
      )}
    </div>
  );
}

function Residuals({
  detail,
  xs,
  ys,
  strings,
  fmt,
}: {
  detail: CurveFit;
  xs: readonly number[];
  ys: readonly number[];
  strings: CurveStrings;
  fmt: NumberFormatter;
}) {
  const num = (value: number) => formatStat(fmt, value);
  const shown = Math.min(xs.length, SHOWN_RESIDUALS);
  return (
    <div className="space-y-2">
      <h3 className={WIDGET_H3}>{strings.residualsTitle}</h3>
      <ResultTable
        head={[
          strings.colIndex,
          strings.colX,
          strings.colY,
          strings.colFitted,
          strings.colResidual,
        ]}
        rows={Array.from({ length: shown }, (_, i) => [
          fmt(i + 1),
          num(xs[i]),
          num(ys[i]),
          num(detail.fitted[i]),
          num(detail.residuals[i]),
        ])}
      />
      {xs.length > shown ? (
        <Explanation>
          {t(strings.residualsMore, {
            shown: fmt(shown),
            total: fmt(xs.length),
          })}
        </Explanation>
      ) : null}
    </div>
  );
}

function Working({
  detail,
  strings,
  fmt,
}: {
  detail: CurveFit;
  strings: CurveStrings;
  fmt: NumberFormatter;
}) {
  const num = (value: number) => formatStat(fmt, value);
  let methodText: string = strings.workExpPower;
  if (polynomialDegree(detail.id) !== null) {
    methodText = strings.workPoly;
  } else if (detail.id === "logarithmic") {
    methodText = strings.workLog;
  }
  return (
    <WorkingPanel title={strings.workingTitle}>
      <Explanation>{methodText}</Explanation>
      <Formula>
        {t(strings.workMetrics, {
          sse: num(detail.sse),
          sst: num(detail.sst),
          r2: num(detail.r2),
        })}
      </Formula>
    </WorkingPanel>
  );
}

export function CurveResults(props: Props) {
  const { results, modelName, strings, fmt, xs, ys } = props;
  const fitted = results.filter((r) => r.fit !== null);
  const [choice, setChoice] = useState("");
  const best = bestOf(fitted);
  const chosen =
    fitted.find((r) => r.id === choice) ?? fitted.find((r) => r.id === best);
  const detail = chosen?.fit ?? null;
  return (
    <>
      <Comparison {...props} />
      {detail && chosen ? (
        <>
          <SelectField
            id="cf-detail"
            label={strings.chooseModel}
            onChange={setChoice}
            options={fitted.map((r) => ({
              value: r.id,
              label: modelName(r.id),
            }))}
            value={chosen.id}
          />
          <Detail
            fit={detail}
            fmt={fmt}
            n={xs.length}
            name={modelName(chosen.id)}
            strings={strings}
          />
          <Plots
            detail={detail}
            fitted={fitted}
            fmt={fmt}
            modelName={modelName}
            strings={strings}
            xs={xs}
            ys={ys}
          />
          <Predict
            fitted={fitted}
            fmt={fmt}
            modelName={modelName}
            strings={strings}
            xs={xs}
          />
          <Residuals
            detail={detail}
            fmt={fmt}
            strings={strings}
            xs={xs}
            ys={ys}
          />
          <Working detail={detail} fmt={fmt} strings={strings} />
        </>
      ) : null}
    </>
  );
}

/** The model id with the lowest AICc (or the first, when none is defined). */
function bestOf(fitted: ModelResult[]): ModelId | null {
  let best: ModelResult | null = null;
  for (const r of fitted) {
    const aicc = (r.fit as CurveFit).aicc;
    if (best === null || aicc < (best.fit as CurveFit).aicc) {
      best = r;
    }
  }
  return best?.id ?? null;
}
